import { useQuery } from '@apollo/client'
import { useMemo, useState } from 'react'

import { useAccounts, UseAccountsReturn } from '../../hooks/use-accounts'
import { BalanceChart } from './balance-chart'
import { GET_CHART_DATA } from './queries'

const weekInSeconds = 604800000

// get beggining of that day from date
export const getBegginingOfDay = (day: Date | undefined) => {
    if (!day) return undefined
    const date = new Date(day.getTime())
    date.setHours(0, 0, 0, 0)
    return date
}

export function BalanceChartContainer() {
    const { accounts, loading: accountsLoading } = useAccounts({
        type: undefined,
    }) as UseAccountsReturn
    const [date, setDate] = useState([
        new Date(new Date().getTime() - weekInSeconds),
        new Date(),
    ])

    const updateDate = ([initialDate, finishDate]: Date[]) => {
        setDate([
            getBegginingOfDay(initialDate) ?? new Date(),
            getBegginingOfDay(finishDate) ?? new Date(),
        ])
    }

    const { cryptoAccountIds, fiatAccountIds } = useMemo(() => {
        const cryptoAccountIds = new Array<string>()
        const fiatAccountIds = new Array<string>()
        accounts.forEach(account => {
            if (account.type && account.id) {
                if (account.type === 'BANK') {
                    fiatAccountIds.push(account.id)
                } else {
                    cryptoAccountIds.push(account.id)
                }
            }
        })
        return {
            cryptoAccountIds,
            fiatAccountIds,
        }
    }, [accounts])

    const { data, loading } = useQuery(GET_CHART_DATA, {
        variables: {
            input: {
                endDate: getBegginingOfDay(date[1]),
                startDate: getBegginingOfDay(date?.[0] ?? undefined),
                cryptoAccountIds,
                fiatAccountIds,
            },
        },
        fetchPolicy: 'network-only',
    })

    const [cryptoData, bankData]: any[] = useMemo(() => {
        if (loading || accountsLoading) {
            return [[], []]
        } else {
            if (data?.getGraphData) {
                return [data.getGraphData.crypto, data.getGraphData.bank]
            } else {
                return [[], []]
            }
        }
    }, [data, loading, accountsLoading])

    const totalBalance: number = useMemo(() => {
        if (loading) return undefined
        else {
            if (data) {
                return data.getGraphData.balance
            } else {
                return undefined
            }
        }
    }, [data, loading])

    return (
        <BalanceChart
            bank={bankData}
            crypto={cryptoData}
            loading={loading}
            date={date}
            setDate={updateDate}
            totalBalance={totalBalance}
        />
    )
}
